<template>
  <div>
    <template v-if="!print_mode">
      <div class="d-flex flex-column w-100">
        <h5 v-if="hasTitle && show_title" class="mb-3 fw-600 gray-800" :style="titleStyle" >
          <span v-if="lang === 'KOR'">
            {{ disclosureInfo.image_title }}
          </span>
          <span v-else>
            {{ disclosureInfo.image_title_em || disclosureInfo.image_title_ea }}
          </span>
        </h5>

        <div align="center" class="d-flex mb-2 text-center" :class="imageAlignmentClass"
          :style="{ overflow: 'visible' }" @click="openImgPopup">
          <b-img v-if="lang === 'KOR'" :style="{ width: disclosureInfo.image_width + '%' }" class="border-radius cursor"
            :src="imageUrl" @load="onImageLoad" />
          <b-img v-else :style="{ width: imageWidth }" class="border-radius cursor" :src="imageUrlE" @load="onImageLoad" />
        </div>

        <div >
          <p class="fw-400 f-90" :style="[ captionStyle, { whiteSpace: 'pre-line' } ]">
            <span v-if="content_caption !== 'null'">
              {{ content_caption }}
            </span>
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <b-row>
        <b-col cols="2" v-if="show_title">
          <h6 v-if="hasTitle" class="mb-3 fw-600 gray-800 border-top-dark pt-2">
            <span v-if="lang === 'KOR'">
              {{ disclosureInfo.image_title }}
            </span>
            <span v-else>
              {{ disclosureInfo.image_title_em || disclosureInfo.image_title_ea }}
            </span>
          </h6>
        </b-col>
        <b-col cols="10" :offset="show_title ? 0 : 2">
          <div align="center" class="d-flex mb-2 text-center" :class="imageAlignmentClass"
            :style="{ overflow: 'visible' }" @click="openImgPopup">
            <b-img v-if="lang === 'KOR'" style=" width:100% " :src="imageUrl" @load="onImageLoad" />
            <b-img v-else style=" width:100% " :src="imageUrlE" @load="onImageLoad" />
          </div>

          <div v-if="hasCaption">
            <p class="fw-400 f-90" style="white-space: pre-line">
              <span v-if="lang === 'KOR'">
                {{ disclosureInfo.image_caption }}
              </span>
              <span v-else>
                {{ disclosureInfo.image_caption_em || disclosureInfo.image_caption_ea }}
              </span>
            </p>
          </div>
        </b-col>
      </b-row>
    </template>
  </div>

</template>

<script>
export default {
  name: 'ImageContent',
  props: {
    disclosureInfo: { type: Object, required: true },
    lang: { type: String, required: true },
    print_mode: { type: Boolean, default: false },
    show_title: { type: Boolean, default: true },
  },
  data() {
    return {
      // 커스텀 src 목록 - 영문일 때 width를 100%로 설정할 이미지들
      customSrcList: [
        '환경경영 전략_eng_2.svg',
        '중장기 온실가스 저감 목표_eng.svg',
        '안전사고 대응 프로세스_eng.svg',
        '안전사고 대응 프로세스_2_eng.svg',
      ]
    };
  },
  computed: {
    // 현재 이미지가 커스텀 목록에 포함되는지 체크
    isCustomImage() {
      const currentImageUrl = this.disclosureInfo.image_url_e || this.disclosureInfo.image_url;
      if (!currentImageUrl) return false;

      return this.customSrcList.some(customSrc => {
        return currentImageUrl.includes(customSrc);
      });
    },

    // 영문이고 커스텀 목록에 포함된 경우 width를 100%로 설정
    imageWidth() {
      if (this.lang !== 'KOR' && this.isCustomImage) {
        return '100%';
      }
      return this.disclosureInfo.image_width + '%';
    },

    hasTitle() {
      return (
        this.disclosureInfo.image_title &&
        this.disclosureInfo.image_title !== 'null' &&
        this.disclosureInfo.image_title !== ' '
      );
    },
    content_caption() {
      return (this.lang !== 'ENG' ? this.disclosureInfo.image_caption : this.disclosureInfo.image_caption_em ?  this.disclosureInfo.image_caption_em : this.disclosureInfo.image_caption_ea)
    },
    hasCaption() {
      return (
        this.disclosureInfo.image_caption &&
        this.disclosureInfo.image_caption !== 'null' &&
        this.disclosureInfo.image_caption !== ' '
      );
    },
    imageAlignmentClass() {
      const alignment = this.disclosureInfo.alignment;
      if (alignment === 'left') {
        return 'justify-content-start';
      } else if (alignment === 'right') {
        return 'justify-content-end';
      } else {
        return 'justify-content-center';
      }
    },
    imageUrl() {
      return this.disclosureInfo.image_url
        ? this.$store.state.static_host +
        this.disclosureInfo.image_url.replace(/\\/g, '/')
        : null;
    },
    imageUrlE() {
      return this.disclosureInfo.image_url_e
        ? this.$store.state.static_host +
        this.disclosureInfo.image_url_e.replace(/\\/g, '/')
        : null;
    },
    imageBoundaryOffsets() {
      const width = this.disclosureInfo.image_width || 100;
      let leftOffset, rightOffset;
      const alignment = this.disclosureInfo.alignment;
      if (alignment === 'left') {
        leftOffset = 0;
        rightOffset = width;
      } else if (alignment === 'center') {
        leftOffset = (100 - width) / 2;
        rightOffset = (100 - width) / 2 + width;
      } else {
        leftOffset = 100 - width;
        rightOffset = 100;
      }
      return { leftOffset: `${leftOffset}%`, rightOffset: `${rightOffset}%` };
    },
    titleStyle() {
      switch (this.disclosureInfo.title_alignment) {
        case 'text-start':   return { textAlign: 'left' };
        case 'center':       return { textAlign: 'center' };
        case 'text-end':     return { textAlign: 'right' };
        case 'image-left':   return { textAlign: 'left', marginLeft: this.imageBoundaryOffsets.leftOffset, marginRight: this.imageBoundaryOffsets.leftOffset };
        case 'image-right':  return { textAlign: 'right', marginLeft: this.imageBoundaryOffsets.leftOffset, marginRight: this.imageBoundaryOffsets.leftOffset };
        default:             return {};
      }
    },
    captionStyle() {
      switch (this.disclosureInfo.caption_alignment) {
        case 'text-start':   return { textAlign: 'left' };
        case 'center':       return { textAlign: 'center' };
        case 'text-end':     return { textAlign: 'right' };
        case 'image-left':   return { textAlign: 'left', marginLeft: this.imageBoundaryOffsets.leftOffset, marginRight: this.imageBoundaryOffsets.leftOffset };
        case 'image-right':  return { textAlign: 'right', marginLeft: this.imageBoundaryOffsets.leftOffset, marginRight: this.imageBoundaryOffsets.leftOffset };
        default:             return {};
      }
    },
  },
  methods: {
    openImgPopup(e) {
      console.log(e);
      const img = e.target;
      if (img.tagName === 'IMG') {
        this.$EventBus.$emit('image-modal', img.getAttribute('src'), true);
      }
    },
    onImageLoad() {
      this.$emit('image-loaded');
    },
  },
};
</script>

<style scoped>
h5, p {
  white-space: pre-wrap;       /* 줄바꿈 문자 유지하면서 자동 줄바꿈 */
  word-wrap: break-word;       /* 단어 단위로 줄바꿈 */
  overflow-wrap: break-word;   /* 단어 단위로 줄바꿈(신규 속성) */
}

/* 이미지 품질 개선 - 부드러운 렌더링 - 이 컴포넌트에서만 적용 */
img {
  /* 고품질 스무딩 활성화 */
  image-rendering: auto;
  image-rendering: -webkit-optimize-contrast;

  /* 브라우저 기본 안티앨리어싱 유지 */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  /* 하드웨어 가속으로 렌더링 품질 향상 */
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  backface-visibility: hidden;

  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

/* Bootstrap Vue 컴포넌트 내부 img 태그 타겟팅 - 이 컴포넌트에서만 적용 */
::v-deep .b-img img {
  image-rendering: auto !important;
  image-rendering: -webkit-optimize-contrast !important;

  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;

  -webkit-backface-visibility: hidden !important;
  -moz-backface-visibility: hidden !important;
  backface-visibility: hidden !important;

  transform: translateZ(0) !important;
  -webkit-transform: translateZ(0) !important;
}
</style>
